"use client";

import React from "react";
import { motion } from "framer-motion";
import { 
  Gift, 
  Share2, 
  Users, 
  Wallet, 
  BadgePercent,
  PartyPopper,
  ArrowRight
} from "lucide-react";
import { Button } from "../../../components/ui/button";
import Link from "next/link";

const steps = [
  {
    icon: <Share2 />,
    title: "Spread the Word",
    description: "Share your unique referral link or just mention Outcome Outing to your tribe—friends, colleagues, or social circles."
  },
  {
    icon: <Users />,
    title: "They Apply & Join",
    description: "When your friend applies and successfully joins one of our curated cohorts."
  },
  {
    icon: <Wallet />,
    title: "You Get Rewarded",
    description: "Earn travel credits or cash rewards for every successful booking made through your referral."
  }
];

const ReferPage = () => {
  return (
    <div className="bg-white min-h-screen font-jakarta">
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#063258] to-[#041e35] py-24 px-6 text-center text-white relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full opacity-5 pointer-events-none">
          {/* Decorative pattern */}
          <div className="absolute top-10 left-10"><PartyPopper size={100} /></div>
          <div className="absolute bottom-10 right-10"><Gift size={100} /></div>
        </div>
        
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-4xl mx-auto relative z-10"
        >
          <div className="inline-block bg-[#FFAE00] text-[#063258] px-6 py-2 rounded-full font-bold mb-8">
            INVITE YOUR TRIBE
          </div>
          <h1 className="text-5xl md:text-7xl font-bricolage font-bold mb-8 leading-tight">
            Travel is better with <span className="text-[#FFAE00]">friends.</span>
          </h1>
          <p className="text-xl md:text-2xl text-white/80 max-w-2xl mx-auto mb-12">
            Refer a friend to Outcome Outing and both of you get exclusive rewards on your next breakthrough journey.
          </p>
          <Link href="https://wa.me/?text=Check%20out%20Outcome%20Outing%21%20They%20organize%20the%20best%20mentor-led%20trips.%20https%3A%2F%2Foutcomeouting.com" target="_blank">
            <Button className="bg-[#FFAE00] hover:bg-orange-500 text-white px-12 py-8 rounded-2xl text-xl font-bold shadow-2xl transition-all flex items-center gap-3 mx-auto">
              Start Referring Now <Share2 className="w-6 h-6" />
            </Button>
          </Link>
        </motion.div>
      </section>

      {/* How it works */}
      <section className="py-24 px-6 max-w-7xl mx-auto">
        <h2 className="text-4xl font-bricolage font-bold text-[#063258] text-center mb-20">How it works?</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {steps.map((step, i) => (
            <div key={i} className="flex flex-col items-center text-center space-y-6">
              <div className="w-20 h-20 bg-gray-50 rounded-3xl flex items-center justify-center text-[#063258] shadow-sm border border-gray-100">
                {React.cloneElement(step.icon, { size: 32 })}
              </div>
              <h3 className="text-2xl font-bricolage font-bold text-[#063258]">{step.title}</h3>
              <p className="text-gray-600 text-lg leading-relaxed">{step.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Rewards Tiers */}
      <section className="py-24 px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="bg-white p-12 rounded-[3.5rem] shadow-xl border border-gray-100 flex flex-col md:flex-row items-center gap-12">
            <div className="flex-1 space-y-6">
              <h2 className="text-4xl font-bricolage font-bold text-[#063258]">What&apos;s in it for you?</h2>
              <div className="space-y-4">
                <div className="flex items-center gap-4 text-lg font-bold text-gray-700">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center text-green-600">
                    <BadgePercent />
                  </div>
                  <span>Flat ₹1000 - ₹5000 travel credits</span>
                </div>
                <div className="flex items-center gap-4 text-lg font-bold text-gray-700">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600">
                    <Gift />
                  </div>
                  <span>Exclusive Early-Bird access to all trips</span>
                </div>
                <div className="flex items-center gap-4 text-lg font-bold text-gray-700">
                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center text-orange-600">
                    <PartyPopper />
                  </div>
                  <span>Special Founder Group Invites</span>
                </div>
              </div>
            </div>
            <div className="bg-[#FFAE00] p-10 rounded-[3rem] text-[#063258] text-center space-y-6 md:w-80">
               <p className="font-bold uppercase tracking-widest text-sm opacity-60">Friend Reward</p>
               <h3 className="text-5xl font-bricolage font-bold italic underline">₹2000 OFF</h3>
               <p className="font-bold leading-tight">Your friend gets an instant discount on their first trip!</p>
            </div>
          </div>
        </div>
      </section>

      {/* Referral CTA Box */}
      <section className="py-24 px-6 text-center max-w-4xl mx-auto">
        <div className="bg-[#063258] rounded-[3rem] p-12 text-white space-y-8">
          <h2 className="text-3xl md:text-5xl font-bricolage font-bold mb-6">Want to know your referral status?</h2>
          <p className="text-xl text-white/70 mb-8">
            Connect with our team to get your personalized tracking link.
          </p>
          <Link href="https://wa.me/917982579691" target="_blank">
            <Button className="bg-white text-[#063258] hover:bg-gray-100 px-12 py-8 rounded-2xl text-xl font-bold flex items-center gap-2 mx-auto">
              WhatsApp Support <ArrowRight className="w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ReferPage;
